import React, { useState, useRef, useEffect } from 'react';
import { AINEDetailsFormProps } from '../types';
import { AINEDetails } from '../../../types/policy';
import azureResourceTypes from '../../../data/azure_resource_types.json';
import { Tooltip } from '../../common/Tooltip';

/**
 * Common existence scope values for dropdown
 */
const EXISTENCE_SCOPES = ['Subscription', 'ResourceGroup'];

/**
 * Validates evaluation delay format (ISO 8601 duration or special values)
 * @param value - The evaluation delay value to validate
 * @returns boolean indicating if the value is valid
 */
const isValidEvaluationDelay = (value: string): boolean => {
  if (!value) return true; // Optional field
  
  // Check for special values
  const specialValues = ['AfterProvisioning', 'AfterProvisioningSuccess', 'AfterProvisioningFailure'];
  if (specialValues.includes(value)) return true;
  
  // Check for ISO 8601 duration format (simplified check)
  const iso8601Pattern = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;
  return iso8601Pattern.test(value);
};

/**
 * AINEDetailsForm Component
 * Handles the configuration of Audit If Not Exists effect details
 */
export const AINEDetailsForm: React.FC<AINEDetailsFormProps> = ({
  details,
  onUpdate
}) => {
  // State for managing dropdown visibility and search
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [filteredTypes, setFilteredTypes] = useState<string[]>([]);

  // Refs for handling clicks outside dropdowns
  const typeDropdownRef = useRef<HTMLDivElement>(null);
  const typeInputRef = useRef<HTMLInputElement>(null);

  /**
   * Handles changes to form fields with proper type safety
   * Constructs object with consistent property order and removes empty optional fields
   */
  const handleFieldChange = (field: keyof AINEDetails, value: string | undefined) => {
    // Start with an empty object and build it in the exact desired order
    const updatedDetails: AINEDetails = {} as AINEDetails;

    // 1. type (always first, required)
    updatedDetails.type = field === 'type' ? (value || '') : (details.type || '');

    // 2. name (optional, only if has value)
    const nameValue = field === 'name' ? value : details.name;
    if (nameValue && nameValue.trim()) {
      updatedDetails.name = nameValue;
    }

    // 3. resourceGroupName (optional, only if has value)
    const resourceGroupValue = field === 'resourceGroupName' ? value : details.resourceGroupName;
    if (resourceGroupValue && resourceGroupValue.trim()) {
      updatedDetails.resourceGroupName = resourceGroupValue;
    }

    // 4. existenceScope (optional, only if has value)
    const existenceScopeValue = field === 'existenceScope' ? value : details.existenceScope;
    if (existenceScopeValue && (existenceScopeValue === 'Subscription' || existenceScopeValue === 'ResourceGroup')) {
      updatedDetails.existenceScope = existenceScopeValue;
    }

    // 5. evaluationDelay (optional, only if has value)
    const evaluationDelayValue = field === 'evaluationDelay' ? value : details.evaluationDelay;
    if (evaluationDelayValue && evaluationDelayValue.trim()) {
      updatedDetails.evaluationDelay = evaluationDelayValue;
    }

    // 6. existenceCondition (always last, required)
    updatedDetails.existenceCondition = details.existenceCondition || {};

    onUpdate(updatedDetails);
  };

  // Filter Azure resource types based on search term
  useEffect(() => {
    if (typeSearchTerm) {
      const filtered = azureResourceTypes.filter(type =>
        type.toLowerCase().includes(typeSearchTerm.toLowerCase())
      );
      setFilteredTypes(filtered.slice(0, 200)); // Show up to 200 matching results
    } else {
      setFilteredTypes(azureResourceTypes.slice(0, 200)); // Show first 200 by default
    }
  }, [typeSearchTerm]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
        setShowTypeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
        <h3 className="flex items-center gap-2 text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          Audit If Not Exists Details
          <Tooltip content="Configure the resource type and conditions to check for existence. These settings determine what Azure Policy will audit for when the condition is met, and determines compliance status." />
        </h3>
      </div>

      {/* Resource Type (Required) */}
      <div className="relative" ref={typeDropdownRef}>
        <label htmlFor="aine-type" className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Resource Type
          <span className="text-red-500">*</span>
          <Tooltip content="Specifies the type of the related resource to match. If type is a resource type underneath the if condition resource, the policy queries for resources of this type within the scope of the evaluated resource. Otherwise, policy queries within the same resource group or subscription as the evaluated resource depending on the existenceScope." />
        </label>
        <input
          ref={typeInputRef}
          id="aine-type"
          type="text"
          value={details.type || ''}
          onChange={(e) => {
            handleFieldChange('type', e.target.value);
            setTypeSearchTerm(e.target.value);
            setShowTypeDropdown(true);
          }}
          onFocus={() => {
            setTypeSearchTerm(details.type || '');
            setShowTypeDropdown(true);
          }}
          placeholder="e.g., Microsoft.Compute/virtualMachines/extensions"
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
          required
        />
        
        {/* Type Dropdown */}
        {showTypeDropdown && (
          <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
            {/* Header with count information */}
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 text-xs text-gray-600 dark:text-gray-400">
              {typeSearchTerm ? (
                <>Showing {filteredTypes.length} matching types {filteredTypes.length >= 200 ? '(limited to 200)' : ''}</>
              ) : (
                <>Showing {filteredTypes.length} of {azureResourceTypes.length} types. Type to search...</>
              )}
            </div>
            
            {filteredTypes.length > 0 ? (
              filteredTypes.map((type, index) => (
                <button
                  key={index}
                  type="button"
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                  onClick={() => {
                    handleFieldChange('type', type);
                    setShowTypeDropdown(false);
                    setTypeSearchTerm('');
                  }}
                >
                  {type}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                No resource types found matching "{typeSearchTerm}"
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resource Name (Optional) */}
      <div>
        <label htmlFor="aine-name" className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Resource Name (Optional)
          <Tooltip content="Specifies the exact name of the resource to match and causes the policy to fetch one specific resource instead of all resources of the specified type. When the condition values for if.field.type and then.details.type match, then name becomes required and must be [field('name')], or [field('fullName')] for a child resource." />
        </label>
        <input
          id="aine-name"
          type="text"
          value={details.name || ''}
          onChange={(e) => handleFieldChange('name', e.target.value)}
          placeholder="e.g., [field('name')] or specific resource name"
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
        />
      </div>

      {/* Resource Group Name (Optional) */}
      <div>
        <label htmlFor="aine-resource-group" className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Resource Group Name (Optional)
          <Tooltip content="Allows the matching of the related resource to come from a different resource group. Doesn't apply if type is a resource that would be underneath the if condition resource. Default is the if condition resource's resource group." />
        </label>
        <input
          id="aine-resource-group"
          type="text"
          value={details.resourceGroupName || ''}
          onChange={(e) => handleFieldChange('resourceGroupName', e.target.value)}
          placeholder="e.g., myResourceGroup"
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
        />
      </div>

      {/* Existence Scope (Optional) */}
      <div>
        <label htmlFor="aine-existence-scope" className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Existence Scope (Optional)
          <Tooltip content="Sets the scope of where to fetch the related resource to match from. Allowed values are Subscription and ResourceGroup. For ResourceGroup, would limit to the resource group in resourceGroupName if specified. For Subscription, queries the entire subscription for the related resource." />
        </label>
        <select
          id="aine-existence-scope"
          value={details.existenceScope || ''}
          onChange={(e) => {
            const value = e.target.value;
            if (value === 'Subscription' || value === 'ResourceGroup') {
              handleFieldChange('existenceScope', value);
            } else {
              handleFieldChange('existenceScope', undefined);
            }
          }}
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm"
        >
          <option value="">Select scope (default: ResourceGroup)</option>
          {EXISTENCE_SCOPES.map(scope => (
            <option key={scope} value={scope}>{scope}</option>
          ))}
        </select>
      </div>

      {/* Evaluation Delay (Optional) */}
      <div>
        <label htmlFor="aine-evaluation-delay" className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Evaluation Delay (Optional)
          <Tooltip content="Specifies the delay between evaluation and effect. Can be an ISO 8601 duration (e.g., PT10M for 10 minutes) or special values like AfterProvisioning, AfterProvisioningSuccess, or AfterProvisioningFailure." />
        </label>
        <input
          id="aine-evaluation-delay"
          type="text"
          value={details.evaluationDelay || ''}
          onChange={(e) => handleFieldChange('evaluationDelay', e.target.value)}
          placeholder="e.g., PT10M, AfterProvisioning, AfterProvisioningSuccess"
          className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border rounded px-3 focus:outline-none focus:ring-2 sm:text-sm ${
            details.evaluationDelay && !isValidEvaluationDelay(details.evaluationDelay)
              ? 'border-red-300 dark:border-red-600 bg-red-50 dark:bg-red-900/20'
              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900'
          } text-gray-900 dark:text-gray-100 focus:ring-blue-500`}
        />
        {details.evaluationDelay && !isValidEvaluationDelay(details.evaluationDelay) && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            Invalid format. Use ISO 8601 duration (e.g., PT10M) or special values (AfterProvisioning, AfterProvisioningSuccess, AfterProvisioningFailure).
          </p>
        )}
      </div>
    </div>
  );
}; 